import { Add, ArticleOutlined, ExpandLess, ExpandMore, Search } from '@mui/icons-material'
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DragEvent, FC, Fragment, useMemo, useState } from 'react'
import { DocPageScope, DocPageStatus, DocPageSummaryFragment } from '../../../generated'
import { DocTreeNode } from '../../interfaceTypes/docs'
import {
  computeDocReorder,
  DocReorderItem,
  DocReorderPosition,
  filterDocTree,
} from '../../utils/docs'
import { DOCS_LABELS } from './docs.constants'

interface DocsTreeSidebarProps {
  nodes: DocTreeNode[]
  flatPages: DocPageSummaryFragment[]
  scope: DocPageScope
  selectedId?: string
  canManage: boolean
  onScopeChange: (scope: DocPageScope) => void
  onSelect: (node: DocTreeNode) => void
  onAddSubPage: (parentId: string) => void
  onAddRoot: () => void
  onReorder: (items: DocReorderItem[]) => void
}

interface DropTarget {
  id: string
  position: DocReorderPosition
}

const StatusBadge: FC<{ status: DocPageStatus }> = ({ status }) => {
  if (status === DocPageStatus.Published) return null
  const isDraft = status === DocPageStatus.Draft
  return (
    <Chip
      size="small"
      label={isDraft ? DOCS_LABELS.DRAFT_BADGE : DOCS_LABELS.ARCHIVED_BADGE}
      color={isDraft ? 'warning' : 'default'}
      sx={{ height: 18, fontSize: 10, ml: 0.5 }}
    />
  )
}

export const DocsTreeSidebar: FC<DocsTreeSidebarProps> = ({
  nodes,
  flatPages,
  scope,
  selectedId,
  canManage,
  onScopeChange,
  onSelect,
  onAddSubPage,
  onAddRoot,
  onReorder,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [search, setSearch] = useState('')
  const [dragId, setDragId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)

  const toggle = (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const searching = search.trim().length > 0
  const { tree: visibleNodes } = useMemo(() => filterDocTree(nodes, search), [nodes, search])
  // Drag-and-drop reordering is only meaningful in the unfiltered tree.
  const dndEnabled = canManage && !searching

  const resetDrag = () => {
    setDragId(null)
    setDropTarget(null)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>, nodeId: string) => {
    if (!dndEnabled || !dragId || dragId === nodeId) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const offset = e.clientY - rect.top
    const third = rect.height / 3
    const position: DocReorderPosition =
      offset < third ? 'before' : offset > rect.height - third ? 'after' : 'inside'
    setDropTarget((prev) =>
      prev?.id === nodeId && prev.position === position ? prev : { id: nodeId, position },
    )
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>, nodeId: string) => {
    if (!dndEnabled || !dragId) return
    e.preventDefault()
    const position = dropTarget?.id === nodeId ? dropTarget.position : 'after'
    const items = computeDocReorder(flatPages, dragId, nodeId, position)
    resetDrag()
    onReorder(items)
  }

  const dropSx = (nodeId: string) => {
    if (!dropTarget || dropTarget.id !== nodeId) return {}
    const accent = theme.palette.primary.main
    if (dropTarget.position === 'before') return { boxShadow: `inset 0 2px 0 0 ${accent}` }
    if (dropTarget.position === 'after') return { boxShadow: `inset 0 -2px 0 0 ${accent}` }
    return {
      bgcolor: 'action.hover',
      outline: `2px dashed ${accent}`,
      outlineOffset: '-2px',
    }
  }

  const renderNode = (node: DocTreeNode) => {
    const hasChildren = node.children.length > 0
    const isOpen = searching || (expanded[node.id] ?? node.depth === 0)
    return (
      <Fragment key={node.id}>
        <ListItemButton
          selected={selectedId === node.id}
          onClick={() => onSelect(node)}
          draggable={dndEnabled}
          onDragStart={() => dndEnabled && setDragId(node.id)}
          onDragEnd={resetDrag}
          onDragOver={(e) => handleDragOver(e, node.id)}
          onDrop={(e) => handleDrop(e, node.id)}
          sx={{
            pl: 1.5 + node.depth * 2,
            pr: 1,
            py: 0.5,
            borderRadius: 1,
            opacity: dragId === node.id ? 0.4 : 1,
            cursor: dndEnabled ? 'grab' : 'pointer',
            // Neutralize the app-wide ".Mui-selected::after" underline, which is
            // positioned below the row and otherwise overlaps the next item.
            '&.Mui-selected': {
              bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.selected' },
              '&::after': { display: 'none' },
            },
            ...dropSx(node.id),
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              edge="start"
              onClick={(e) => {
                e.stopPropagation()
                toggle(node.id)
              }}
              sx={{ mr: 0.5 }}
            >
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <ArticleOutlined fontSize="small" sx={{ mr: 1, ml: 0.5, color: 'text.disabled' }} />
          )}
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: selectedId === node.id ? 600 : 400 }}>
                  {node.icon ? `${node.icon} ` : ''}
                  {node.title || DOCS_LABELS.UNTITLED}
                </Typography>
                <StatusBadge status={node.status} />
              </Box>
            }
          />
          {canManage && (
            <Tooltip title={DOCS_LABELS.NEW_SUBPAGE}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setExpanded((prev) => ({ ...prev, [node.id]: true }))
                  onAddSubPage(node.id)
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>{node.children.map(renderNode)}</List>
          </Collapse>
        )}
      </Fragment>
    )
  }

  const emptyMessage = searching
    ? DOCS_LABELS.EMPTY_SEARCH
    : scope === DocPageScope.Personal
      ? DOCS_LABELS.EMPTY_PERSONAL_TREE
      : DOCS_LABELS.EMPTY_TREE

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {DOCS_LABELS.TITLE}
        </Typography>
        {canManage && (
          <Tooltip title={DOCS_LABELS.NEW_PAGE}>
            <IconButton size="small" onClick={onAddRoot}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <ToggleButtonGroup
        value={scope}
        exclusive
        size="small"
        fullWidth
        onChange={(_e, next) => next && onScopeChange(next)}
        sx={{ px: 1, mb: scope === DocPageScope.Personal ? 0.5 : 1 }}
      >
        <ToggleButton value={DocPageScope.Org} sx={{ textTransform: 'none', py: 0.5 }}>
          {DOCS_LABELS.SCOPE_ORG}
        </ToggleButton>
        <ToggleButton value={DocPageScope.Personal} sx={{ textTransform: 'none', py: 0.5 }}>
          {DOCS_LABELS.SCOPE_PERSONAL}
        </ToggleButton>
      </ToggleButtonGroup>

      {scope === DocPageScope.Personal && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, mb: 1 }}>
          {DOCS_LABELS.SCOPE_PERSONAL_HINT}
        </Typography>
      )}

      <Box sx={{ px: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={DOCS_LABELS.SEARCH_PLACEHOLDER}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {visibleNodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <List disablePadding>{visibleNodes.map(renderNode)}</List>
      )}
    </Box>
  )
}
